import React, { useState } from 'react';
import { 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  useMediaQuery, 
  useTheme, 
  InputAdornment
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function PasswordRecoveryCode() {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  
  const email = location.state?.email || '';

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (!code) {
      setErrorMessage('Please enter the verification code.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/password/verify-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setSuccessMessage('Code verified successfully!');
        // Redireciona para a página de nova password após 1 segundo
        setTimeout(() => {
          navigate('/password-reset', { state: { email, code } });
        }, 1000);
      } else {
        setErrorMessage(data.error || 'Invalid verification code.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setErrorMessage('Error verifying code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      fontFamily: "'Poppins', sans-serif",
    },
    leftSection: {
      backgroundImage: "url('app2.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      color: 'rgb(0, 0, 0)',
      padding: '20px',
      minHeight: '100vh',
    },

    leftSectionFooter: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      backdropFilter: 'blur(5px)',
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      padding: '10px 20px',
      borderRadius: '10px',
      fontSize: '14px',
      width: '80%',
      maxWidth: '300px',
    },
    typographyH4: {
      fontWeight: 'bold',
      marginBottom: '10px',
      marginTop: '10px',
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      width: '100%',
    },
    logo: {
      width: '80px',
      marginBottom: '20px',
    },
    formContainer: {
      width: '100%',
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    titleContainer: {
      textAlign: 'center',
      marginBottom: '20px',
      width: '100%',
    },
    button: {
      width: '100%',
      marginTop: '20px',
      backgroundColor: '#007bff',
      color: 'white',
      textTransform: 'none',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
      '&:hover': {
        backgroundColor: '#0056b3',
      },
    },
    backButton: {
      width: '100%',
      marginTop: '10px',
      textTransform: 'none',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
    },
    errorMessage: {
      marginTop: '10px',
      color: 'red',
      fontSize: '14px',
      textAlign: 'center',
    },
    successMessage: {
      marginTop: '10px',
      color: 'green',
      fontSize: '14px',
      textAlign: 'center',
    },
  };

  return (
    <Grid container style={styles.container}>
      {/* Seção da esquerda - MESMO LAYOUT */}
      <Grid
        item
        xs={12}
        sm={6}
        style={styles.leftSection}
      >
         <Typography 
          variant="h3" 
          gutterBottom
          style={{
            color: '#000000',
            textShadow: `
              0 0 10px #fff,
              0 0 10px #fff,
              0 0 10px #fff,
              0 0 10px #fff
            `,
            fontWeight: 'bold',
            marginBottom: '10px'
          }}
        >
          Verify Your Code
        </Typography>
        <Typography 
          variant="h5" 
          gutterBottom
          style={{
            color: '#000000',
            textShadow: `
              0 0 8px #fff,
              0 0 8px #fff,
              0 0 8px #fff
            `,
            fontWeight: '600',
            marginBottom: '20px'
          }}
        >
         Write the code that we sent to you.
        </Typography>

        {/* Texto no fundo */}
        <div style={styles.leftSectionFooter}>
          <Typography variant="body2">
            Remember your password?{' '}
            <Link to="/" style={{ color: '#007bff', textDecoration: 'none' }}>
              Back to Login
            </Link>
          </Typography>
        </div>
      </Grid>

      {/* Seção da direita - MESMO LAYOUT */}
      <Grid
        item
        xs={12}
        sm={6}
        style={styles.rightSection}
      >
        <div style={styles.formContainer}>
          {/* Container para título e logo CENTRALIZADO - MESMO LAYOUT */}
          <div style={styles.titleContainer}>
            <img
              src="/logotipo.png"
              alt="Hub4Food Logo"
              style={{ ...styles.logo, width: isMobile ? '60px' : '80px' }}
            />
            <Typography variant="h5" gutterBottom>
              Enter Verification Code
            </Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: '20px' }}>
              We sent a 4-digit code to: <strong>{email}</strong>
            </Typography>
          </div>

          {/* Formulário para código */}
          <form onSubmit={handleVerifyCode} style={{ width: '100%' }}>
            <TextField
              label="Verification Code"
              variant="outlined"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              margin="normal"
              placeholder="Enter 4-digit code"
              inputProps={{ maxLength: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              style={styles.button}
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/password-recovery')}
              style={styles.backButton}
            >
              Back to Email
            </Button>
          </form>

          {errorMessage && (
            <Typography style={styles.errorMessage}>
              {errorMessage}
            </Typography>
          )}

          {successMessage && (
            <Typography style={styles.successMessage}>
              {successMessage}
            </Typography>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

export default PasswordRecoveryCode;